import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { jwtDecode } from 'jwt-decode';

import { msalInstance } from './msalInstance';
import LoginPage from './pages/LoginPage';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import PatchList from './pages/PatchList';
import PatchForm from './pages/PatchForm';
import ScheduledJobs from './pages/ScheduledJobs';
import EmailLogs from './pages/EmailLogs';
import CustomerList from './pages/CustomerList';
import CustomerDetail from './pages/CustomerDetail';
import EnvironmentSummary from './pages/EnvironmentSummary';
import Users from './pages/Users';
import WafMonitor from './pages/WafMonitor';
import BackupReports from './pages/BackupReports';
import SystemRequests from './pages/SystemRequests';
import LoginHistory from './pages/LoginHistory';
import CustomerLicenses from './pages/CustomerLicenses';
import AppLogs from './pages/AppLogs';
import LogSources from './pages/LogSources';
import CustomerLogs from './pages/CustomerLogs';
import LicensingWhitelist from './pages/LicensingWhitelist';
import SftpAccess from './pages/SftpAccess';
import ServerResources from './pages/ServerResources';
import Uptime from './pages/Uptime';
import DatabaseServers from './pages/DatabaseServers';
import MonitorServers from './pages/MonitorServers';
import TelegrafFleet from './pages/TelegrafFleet';
import ServiceActions from './pages/ServiceActions';
import FailedLogins from './pages/FailedLogins';
import { loginAPI } from './services/api';
import { KnowledgeBaseList, KnowledgeBaseArticle } from './pages/KnowledgeBase';
import Changelog from './pages/Changelog';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Helper: decode OTP token
function getOtpSession() {
  const token = localStorage.getItem('otp_token');
  if (!token) return null;
  try {
    const payload = jwtDecode(token);
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.removeItem('otp_token');
      return null;
    }
    return { email: payload.email, role: payload.role || 'admin', auth: 'otp' };
  } catch {
    localStorage.removeItem('otp_token');
    return null;
  }
}

function UnauthorizedPage({ email, authType }) {
  const { instance } = useMsal();

  const handleLogout = async () => {
    if (authType === 'entra') {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } else {
      localStorage.removeItem('otp_token');
      window.location.reload();
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      p: 3
    }}>
      <Typography variant="h4" gutterBottom>
        Access Denied
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, textAlign: 'center' }}>
        Your account ({email}) is not authorized to access this portal. Please contact your administrator to request access.
      </Typography>
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" onClick={handleLogout} sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
          Logout
        </Typography>
      </Box>
    </Box>
  );
}

function AppContent() {
  const { accounts, instance } = useMsal();
  const entraAuth = useIsAuthenticated();
  const [otpSession, setOtpSession] = useState(() => getOtpSession());

  // Determine active auth
  let authCtx = { role: 'admin', email: '', auth: '' };

  if (otpSession) {
    authCtx = otpSession;
  } else if (entraAuth && accounts.length > 0) {
    const account = accounts[0];
    authCtx = {
      email: account.username,
      role: 'admin',
      auth: 'entra',
    };
  }

  // Not authenticated → show login
  if (!entraAuth && !otpSession) {
    return <LoginPage onOtpToken={(token) => setOtpSession(getOtpSession())} />;
  }

  // Authenticated → show main app with permission check
  return (
    <PermissionsProvider userEmail={authCtx.email}>
      <PermissionGate email={authCtx.email} authType={authCtx.auth} />
    </PermissionsProvider>
  );
}

function PermissionGate({ email, authType }) {
  const { role, loading } = usePermissions();

  // Record this sign-in once per browser session (one-time-code sign-ins are recorded by the server itself)
  useEffect(() => {
    if (loading || authType !== 'entra' || !email) return;
    const key = `login_recorded_${email}`;
    try { if (sessionStorage.getItem(key)) return; } catch (e) { /* storage unavailable: record anyway */ }
    loginAPI.session()
      .then(() => { try { sessionStorage.setItem(key, '1'); } catch (e) { /* ignore */ } })
      .catch(() => {});
  }, [loading, authType, email]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Check if user is unauthorized or inactive
  if (role === 'unauthorized' || role === 'inactive' || !role) {
    return <UnauthorizedPage email={email} authType={authType} />;
  }

  // User has valid role → show main app
  return (
    <Router>
      <Box sx={{ display: 'flex' }}>
        <Sidebar userEmail={email} authType={authType} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            mt: 8,
            backgroundColor: '#f5f5f5',
            minHeight: '100vh',
          }}
        >
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/patches" element={<PatchList />} />
            <Route path="/patches/new" element={<PatchForm />} />
            <Route path="/patches/:id" element={<PatchForm />} />
            <Route path="/customers" element={<CustomerList />} />
            <Route path="/customers/:id" element={<CustomerDetail />} />
            <Route path="/environments" element={<EnvironmentSummary />} />
            <Route path="/scheduled-jobs" element={<ScheduledJobs />} />
            <Route path="/email-logs" element={<EmailLogs />} />
            <Route path="/backups" element={<BackupReports />} />
            <Route path="/failed-logins" element={<FailedLogins />} />
            <Route path="/system-requests" element={<SystemRequests />} />
            <Route path="/kb" element={<KnowledgeBaseList />} />
            <Route path="/kb/:id" element={<KnowledgeBaseArticle />} />
            <Route path="/waf" element={<WafMonitor />} />
              <Route path="/users" element={<Users />} />
            <Route path="/login-history" element={<LoginHistory />} />
            <Route path="/licenses" element={<CustomerLicenses />} />
            <Route path="/app-logs" element={<AppLogs />} />
            <Route path="/log-sources" element={<LogSources />} />
            <Route path="/customer-logs" element={<CustomerLogs />} />
            <Route path="/licensing-whitelist" element={<LicensingWhitelist />} />
            <Route path="/sftp-access" element={<SftpAccess />} />
            <Route path="/server-resources" element={<ServerResources />} />
            <Route path="/uptime" element={<Uptime />} />
            <Route path="/database-servers" element={<DatabaseServers />} />
            <Route path="/monitor-servers" element={<MonitorServers />} />
            <Route path="/telegraf-fleet" element={<TelegrafFleet />} />
            <Route path="/service-actions" element={<ServiceActions />} />
            <Route path="/changelog" element={<Changelog />} />
          </Routes>
        </Box>
      </Box>
    </Router>
  );
}

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </MsalProvider>
  );
}

export default App;
